'use client';

import * as React from 'react';
import { SSO_ENABLED } from '@/config/env';
import { getAccessToken, isHostInjected } from './token';
import { bootstrap, redirectToLogin } from './sso-client';

/**
 * Scoped auth bootstrap for the surfaces that need a real identity (create,
 * salesperson-join) — deliberately NOT a global provider wrapping the whole
 * app, so the public lead-join path never runs any SSO logic at all.
 *
 * `shouldRun` (default true) lets a shared component gate this per-role —
 * e.g. JoinExperience calls `useAuthBootstrap(isHost)` so a lead's browser
 * never fires the `/auth/refresh` bootstrap call at all, even when
 * SSO_ENABLED is on globally. When `shouldRun` is false, or `SSO_ENABLED`
 * is false (the default until Meet is live on a real
 * `.app.growithnsi.com` host), this is a no-op that immediately reports
 * "ready, no token" — today's open behavior, byte-for-byte unchanged.
 *
 * Inside our own mobile app's WebView (`isHostInjected()`), the native app
 * already owns a real, independently-refreshed session and pushes it in via
 * `window.__NSI_MEET_SET_TOKEN__` (token.ts) — this hook then just listens
 * for that instead of ever attempting the cookie-based `bootstrap()`, which
 * cannot work across the WebView's isolated cookie jar (application/
 * components/AppWebView.tsx).
 */
export function useAuthBootstrap(shouldRun = true) {
  const active = SSO_ENABLED && shouldRun;
  const hostInjected = active && isHostInjected();
  const [status, setStatus] = React.useState<'checking' | 'ready' | 'signed-out'>(
    active ? 'checking' : 'ready',
  );

  React.useEffect(() => {
    if (!active) return;

    if (hostInjected) {
      if (getAccessToken()) {
        setStatus('ready');
      }
      const onToken = (e: Event) => {
        setStatus((e as CustomEvent<string | null>).detail ? 'ready' : 'signed-out');
      };
      window.addEventListener('nsimeet:token', onToken);
      return () => window.removeEventListener('nsimeet:token', onToken);
    }

    // Local-dev bypass (NEXT_PUBLIC_DEV_TOKEN) already has a token with no
    // network call needed.
    if (getAccessToken()) {
      setStatus('ready');
      return;
    }
    let cancelled = false;
    bootstrap().then((ok) => {
      if (cancelled) return;
      setStatus(ok ? 'ready' : 'signed-out');
    });
    return () => {
      cancelled = true;
    };
  }, [active, hostInjected]);

  return {
    enabled: active,
    status,
    token: getAccessToken(),
    redirectToLogin,
  };
}
